/**
 * Cube REST client. Routes every request through dash-prime worker which
 * handles JWT verification and Cube basic_auth injection server-side.
 *
 * Build-time constant `__CUBE_PROXY_URL__` is set by vite.config.ts.
 */

import { getJWT } from './jwt.js';

declare const __CUBE_PROXY_URL__: string;

export interface CubeFilter {
  member: string;
  operator: 'equals' | 'notEquals' | 'in' | 'notIn' | 'gt' | 'gte' | 'lt' | 'lte' | 'set' | 'notSet' | 'contains';
  values?: (string | number | null)[];
}

export interface CubeTimeDimension {
  dimension: string;
  granularity?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  dateRange?: string | [string, string];
}

export interface CubeQuery {
  measures?: string[];
  dimensions?: string[];
  filters?: CubeFilter[];
  timeDimensions?: CubeTimeDimension[];
  order?: Record<string, 'asc' | 'desc'>;
  limit?: number;
  offset?: number;
  segments?: string[];
}

export interface CubeLoadResponse<T = Record<string, unknown>> {
  data: T[];
  annotation?: unknown;
}

export class CubeAuthError extends Error {}
export class CubeNetworkError extends Error {}
export class CubeQueryError extends Error {
  constructor(message: string, public status: number) { super(message); }
}

export async function cubeLoad<T = Record<string, unknown>>(
  query: CubeQuery,
  signal?: AbortSignal,
): Promise<T[]> {
  const token = getJWT();
  if (!token) throw new CubeAuthError('No JWT in URL fragment. Expected #t=<jwt>.');

  let res: Response;
  try {
    res = await fetch(`${__CUBE_PROXY_URL__}/load`, {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ query }),
      signal,
    });
  } catch (e) {
    throw new CubeNetworkError(`Network error calling Cube proxy: ${(e as Error).message}`);
  }

  if (res.status === 401 || res.status === 403) {
    throw new CubeAuthError(`Cube auth rejected (${res.status})`);
  }
  if (!res.ok) {
    const text = await res.text();
    throw new CubeQueryError(`Cube error ${res.status}: ${text.slice(0, 300)}`, res.status);
  }

  const json = (await res.json()) as CubeLoadResponse<T>;
  return json.data;
}

export async function cubeMeta(signal?: AbortSignal): Promise<unknown> {
  const token = getJWT();
  if (!token) throw new CubeAuthError('No JWT in URL fragment.');
  const res = await fetch(`${__CUBE_PROXY_URL__}/meta`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (!res.ok) throw new CubeQueryError(`meta failed: ${res.status}`, res.status);
  return res.json();
}
